import { DexProtocol, SwapQuoteResponse } from "@cede/types";
import { DEFAULT_DEX, DEFAULT_SLIPPAGE } from "@/constants/onchain-swap";
import Decimal from "decimal.js";
import { create } from "zustand";

interface OnchainSwapState {
  // DEX selection
  selectedDex: DexProtocol;

  // Token selection - store addresses only (single source of truth)
  // Full token objects are derived from useSwapTokens query
  tokenInAddress: string | null;
  tokenOutAddress: string | null;

  // Amounts
  amountIn: string;
  estimatedAmountOut: string;

  // Settings
  slippage: Decimal;
  chainId: string; // String to support both EVM ("8453") and non-EVM ("solana")

  // Campaign selection (for volume counting)
  selectedCampaignId: string | null;

  // Campaign pool context (for enforcing pool in quotes)
  campaignPoolAddress: string | null; // Pool address from campaign (null or "all" = any pool)
  campaignDexTokenAddress: string | null; // Token address that must be involved in trade

  // Quote data
  quoteData: SwapQuoteResponse | null;

  // Actions
  setSelectedDex: (dex: DexProtocol) => void;
  setTokenInAddress: (address: string | null) => void;
  setTokenOutAddress: (address: string | null) => void;
  setAmountIn: (amount: string) => void;
  setSlippage: (slippage: Decimal) => void;
  setChainId: (chainId: string) => void;
  setSelectedCampaignId: (campaignId: string | null) => void;
  setCampaignPoolContext: (poolAddress: string | null, dexTokenAddress: string | null) => void;
  swapTokens: () => void; // Swap tokenIn <-> tokenOut
  reset: () => void;
  resetAndSetTokens: (tokenInAddress: string, tokenOutAddress: string) => void; // Atomic reset + set
}

export const useOnchainSwapStore = create<OnchainSwapState>((set, get) => ({
  selectedDex: DEFAULT_DEX,
  tokenInAddress: null,
  tokenOutAddress: null,
  amountIn: "",
  estimatedAmountOut: "",
  slippage: DEFAULT_SLIPPAGE,
  chainId: "8453", // Base by default (string to support both EVM and non-EVM chains)
  selectedCampaignId: null,
  campaignPoolAddress: null,
  campaignDexTokenAddress: null,
  quoteData: null,

  setSelectedDex: (dex) => set({ selectedDex: dex }),
  setTokenInAddress: (address) => set({ tokenInAddress: address, amountIn: "" }), // Clear amount when changing sell token
  setTokenOutAddress: (address) => set({ tokenOutAddress: address }),
  setAmountIn: (amount) => set({ amountIn: amount }),
  setSlippage: (slippage) => set({ slippage }),
  setChainId: (chainId) => set({ chainId }),
  setSelectedCampaignId: (campaignId) => set({ selectedCampaignId: campaignId }),
  setCampaignPoolContext: (poolAddress, dexTokenAddress) =>
    set({
      campaignPoolAddress: poolAddress,
      campaignDexTokenAddress: dexTokenAddress,
    }),

  swapTokens: () => {
    const { tokenInAddress, tokenOutAddress } = get();
    set({
      tokenInAddress: tokenOutAddress,
      tokenOutAddress: tokenInAddress,
      amountIn: "", // Clear amount when swapping
    });
  },

  reset: () =>
    set({
      tokenInAddress: null,
      tokenOutAddress: null,
      amountIn: "",
      estimatedAmountOut: "",
      quoteData: null,
    }),

  resetAndSetTokens: (tokenInAddress, tokenOutAddress) =>
    set({
      tokenInAddress,
      tokenOutAddress,
      amountIn: "",
      estimatedAmountOut: "",
      quoteData: null,
    }),
}));
